import { HelpPopover } from "@/components/ui/help-popover";
import {
  ArrowDownIcon,
  FaroButton,
  FaroSwitch,
  FaroText,
  grey,
  TextField,
  TranslateVar,
} from "@faro-lotv/flat-ui";
import { Box, Collapse } from "@mui/material";
import { Stack } from "@mui/system";
import { useCallback, useState, type JSX } from "react";

/** State representing additional export options */
/** State for additional export settings */
export type ScansExportSettingsState = {
  /** Adds panoramas to the export if true else, omits them if false */
  includePanoramas: boolean;

  /** Exports intensity values if true else, omits them if false */
  includeIntensity: boolean;

  /** Includes invalid points if true else, omits them if false */
  includeInvalidPoints: boolean;

  /** Exports color information if true else, omits it if false */
  includeColor: boolean;

  /** Includes row/col indices if true else, omits them if false */
  includeIndices: boolean;

  /** Reduces point density by keeping every nth point (1 = no subsampling) */
  subsampling: number;

  /** Applies non-standard rotation to panoramas if true else, leaves as-is if false */
  enableNonStandardRotation: boolean;
};

/** Default export settings to fine tune the resulting data */
export const DEFAULT_EXPORT_SETTINGS_VALUES: ScansExportSettingsState = {
  includePanoramas: true,
  enableNonStandardRotation: false,
  includeIntensity: true,
  includeInvalidPoints: false,
  includeColor: true,
  includeIndices: true,
  subsampling: 1,
};

/** The limit to maximum allowed subsampling to keep the resulting data still meaningful */
export const SUBSAMPLING_MAX_VALUE = 20;

type ScansExportSettingsProps = {
  /** Current state of export settings */
  settings: ScansExportSettingsState;

  /** Callback when any settings changes */
  onSettingsChange(options: ScansExportSettingsState): void;
};

/** @returns Additional scans export options as form elements */
export function ScansExportSettings({
  settings,
  onSettingsChange: onConfigsChange,
}: ScansExportSettingsProps): JSX.Element {
  const [isSubsamplingError, setIsSubsamplingError] = useState(false);
  const [isAdvancedSettingsOpen, setIsAdvancedSettingsOpen] = useState(false);

  const toggleConfig = useCallback(
    (optionKey: keyof Omit<ScansExportSettingsState, "subsampling">) => {
      const newSettings = {
        ...settings,
        [optionKey]: !settings[optionKey],
      };
      onConfigsChange(newSettings);
    },
    [settings, onConfigsChange],
  );

  const handleSubsamplingFactorChange = useCallback(
    (value: string) => {
      const numericValue = parseInt(value, 10);
      const newSettings = {
        ...settings,
        subsampling: numericValue,
      };
      onConfigsChange(newSettings);

      if (
        isNaN(numericValue) ||
        numericValue < 1 ||
        numericValue > SUBSAMPLING_MAX_VALUE
      ) {
        setIsSubsamplingError(true);
      } else {
        setIsSubsamplingError(false);
      }
    },
    [settings, onConfigsChange],
  );

  return (
    <Stack sx={{ gap: 3 }}>
      {/* Always show the panorama switch */}
      <Stack sx={{ gap: 1 }}>
        <FaroSwitch
          checked={settings.includePanoramas}
          label={<FaroText variant="bodyM">360° images</FaroText>}
          onToggle={() => toggleConfig("includePanoramas")}
          fullWidth
        />
        {settings.includePanoramas && (
          <FaroSwitch
            checked={settings.enableNonStandardRotation}
            label={
              <Stack
                direction="row"
                sx={{ alignItems: "center", gap: 1, ml: 2 }}
              >
                <FaroText variant="bodyM">Non-standard rotation</FaroText>
                <Box component="div" onClick={(e) => e.stopPropagation()}>
                  <HelpPopover
                    title="Non-standard rotation"
                    variant="darkInfo"
                    description={
                      <FaroText variant="bodyS" color={grey[300]}>
                        Exports panorama images rotated by 180 degrees for
                        compatibility with third-party applications that require
                        this orientation, such as Autodesk ReCap and Prevu3D.
                      </FaroText>
                    }
                  />
                </Box>
              </Stack>
            }
            onToggle={() => toggleConfig("enableNonStandardRotation")}
            fullWidth
          />
        )}
      </Stack>

      <FaroButton
        variant="ghost"
        onClick={() => setIsAdvancedSettingsOpen((prev) => !prev)}
        fullWidth
        sx={{
          p: 0,
          ":hover": { backgroundColor: "inherit" },
          "& .MuiStack-root": {
            width: "100%",
            flexDirection: "row",
            justifyContent: "space-between",
          },
        }}
      >
        <FaroText variant="labelM">Advanced settings</FaroText>
        <ArrowDownIcon
          sx={{
            width: "16px",
            height: "16px",
            transition: "transform 0.2s",
            ...(isAdvancedSettingsOpen && { transform: "rotate(180deg)" }),
          }}
        />
      </FaroButton>

      {/* Advanced settings */}
      <Collapse in={isAdvancedSettingsOpen}>
        <Stack sx={{ gap: 3 }}>
          <FaroSwitch
            checked={settings.includeIntensity}
            label={<FaroText variant="bodyM">Intensity values</FaroText>}
            onToggle={() => toggleConfig("includeIntensity")}
            fullWidth
          />
          <FaroSwitch
            checked={settings.includeInvalidPoints}
            label={
              <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                <FaroText variant="bodyM">Invalid points</FaroText>
                <Box component="div" onClick={(e) => e.stopPropagation()}>
                  <HelpPopover
                    title="Invalid points"
                    variant="darkInfo"
                    description={
                      <FaroText variant="bodyS" color={grey[300]}>
                        If the '360° images' option is disabled, enabling this
                        setting can help to retain color information in areas
                        without 3D data such as the sky.
                      </FaroText>
                    }
                  />
                </Box>
              </Stack>
            }
            onToggle={() => toggleConfig("includeInvalidPoints")}
            fullWidth
          />
          <FaroSwitch
            checked={settings.includeColor}
            label={<FaroText variant="bodyM">Color values</FaroText>}
            onToggle={() => toggleConfig("includeColor")}
            fullWidth
          />
          <TextField
            label={
              <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                <FaroText variant="bodyM">Subsampling factor</FaroText>
                <Box component="div" onClick={(e) => e.stopPropagation()}>
                  <HelpPopover
                    title="Subsampling factor"
                    variant="darkInfo"
                    description={
                      <FaroText variant="bodyS" color={grey[300]}>
                        Millimeter-based subsampling will be supported in a
                        future release.
                      </FaroText>
                    }
                  />
                </Box>
              </Stack>
            }
            text={settings.subsampling.toString()}
            onTextChanged={handleSubsamplingFactorChange}
            type="number"
            placeholder="Enter a subsampling factor"
            helpText={
              <>
                Factor by which to reduce point density (1 = no subsampling,{" "}
                <TranslateVar name="maxSubsamplingValue">
                  {SUBSAMPLING_MAX_VALUE}
                </TranslateVar>{" "}
                = max subsampling)
              </>
            }
            error={
              isSubsamplingError
                ? `Please enter a value between 1 and ${SUBSAMPLING_MAX_VALUE}`
                : undefined
            }
            fullWidth
            inputProps={{
              step: 1,
            }}
          />
        </Stack>
      </Collapse>
    </Stack>
  );
}
